import { memo, useCallback, type KeyboardEvent } from "react";
import { CameraOff, MonitorOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRoomController, useRoomState } from "../../room/RoomContext";
import { initials } from "../../room/initials";
import { pointIsOnRenderedVideo, type StreamTileView } from "../../room/stream-tiles";
import { cn } from "../../lib/utils";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";

const StreamTile = memo(function StreamTile({
  tile,
  focusActive,
}: {
  tile: StreamTileView;
  focusActive: boolean;
}) {
  const { t } = useTranslation();
  const controller = useRoomController();
  const { surfaceId, name, kind, local, focused, streamEnded, visibleVideo } = tile;
  // Neither changing UI props nor CSS order unregisters/remounts this video.
  const registerVideo = useCallback(
    (video: HTMLVideoElement | null) => {
      controller.registerStreamVideo(surfaceId, video);
    },
    [controller, surfaceId],
  );
  const toggleFocus = () => controller.toggleStreamFocus(surfaceId);
  const keyboardToggle = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleFocus();
    }
  };
  const surfaceLabel = t(kind === "screen" ? "common.screenShare" : "common.camera");
  const actionLabel = t(focused ? "tile.returnFrom" : "tile.focus", {
    name,
    kind: surfaceLabel.toLowerCase(),
  });
  const feedback = tile.feedback;
  const feedbackText =
    feedback?.state === "testing"
      ? t("connection.testingDirect")
      : feedback?.state === "retrying"
        ? t("connection.retryingDirect", { attempt: feedback.attempt, max: feedback.maxAttempts })
        : feedback?.state === "failed"
          ? t("connection.directUnavailable")
          : "";
  const EndedIcon = kind === "camera" ? CameraOff : MonitorOff;
  const endedTitle = t(kind === "camera" ? "camera.stoppedTitle" : "screen.stoppedTitle");
  const endedNotice = t("tile.waitingToResume", { name });
  return (
    <article
      className={cn(
        "stream-tile relative aspect-video min-w-0 overflow-hidden rounded-md border-2 border-zinc-900 bg-black transition-[border-color] duration-130 outline-none hover:border-white/[.18]",
        `${kind}-surface`,
        local && "local",
        "has-media",
        tile.watching && "watching",
        tile.connecting && "connecting",
        tile.connectionFailed && "connection-failed",
        streamEnded && "stream-ended bg-zinc-950",
        focused && "focused",
        visibleVideo && "playing",
        focusActive && !focused && "hidden",
        focusActive &&
          focused &&
          "absolute top-0 bottom-(--focus-tile-bottom) z-0 aspect-auto h-auto w-auto border-0 group-data-[focus-layout=camera]/stage:bottom-(--focus-tile-bottom-camera) group-data-[focus-layout=hidden]/stage:bottom-0",
        focusActive &&
          focused &&
          (kind === "screen"
            ? "inset-x-0"
            : "inset-x-[clamp(0.75rem,8vw,4.75rem)] max-[53.75rem]:inset-x-4.5 max-[40.625rem]:inset-x-2"),
      )}
      data-surface-id={surfaceId}
      data-peer-id={tile.id}
      data-kind={kind}
      style={{ order: tile.order }}
      role="group"
      aria-label={`${name}. ${surfaceLabel}.${streamEnded ? ` ${t("tile.sharingStopped")}` : ""}${focused ? ` ${t("tile.focused")}` : ""}`}
      aria-current={focused ? "true" : "false"}
      onPointerMove={() => {
        if (focused) controller.revealFocusedOverlays();
      }}
      onContextMenu={(event) => {
        if (local) return;
        event.preventDefault();
        controller.openStreamContextMenu(tile.id, event.clientX, event.clientY);
      }}
    >
      <video
        ref={registerVideo}
        autoPlay
        playsInline
        controls={false}
        muted={local}
        hidden={!visibleVideo}
        tabIndex={0}
        role="button"
        aria-label={actionLabel}
        className={cn(
          "media-video absolute inset-0 z-1 block h-full w-full cursor-default bg-black object-contain focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary focus-visible:outline-solid [&::-webkit-media-controls]:hidden [&::-webkit-media-controls-enclosure]:hidden",
          local && kind === "camera" && "-scale-x-100",
        )}
        onClick={(event) => {
          if (pointIsOnRenderedVideo(event.currentTarget, event.clientX, event.clientY))
            toggleFocus();
        }}
        onPointerMove={(event) => {
          event.currentTarget.style.cursor = pointIsOnRenderedVideo(
            event.currentTarget,
            event.clientX,
            event.clientY,
          )
            ? "pointer"
            : "default";
        }}
        onKeyDown={keyboardToggle}
      />
      <div
        hidden={visibleVideo || streamEnded}
        className={cn(
          "member-placeholder absolute inset-0 z-1 grid place-content-center justify-items-center gap-4 bg-card p-6",
          focusActive && focused && "bg-black",
        )}
      >
        <div
          className={cn(
            "member-avatar relative grid aspect-square w-[clamp(4.25rem,21%,7rem)] place-items-center rounded-full bg-card text-lg font-extrabold text-white md:text-2xl",
            tile.connecting &&
              "after:absolute after:-inset-2 after:animate-spin after:rounded-full after:border-3 after:border-transparent after:border-t-indigo-300 after:content-[''] after:[animation-duration:.9s]",
          )}
        >
          <Avatar className="grid size-full overflow-visible">
            <AvatarImage
              src={tile.picture || undefined}
              alt=""
              referrerPolicy="no-referrer"
              className="block rounded-[inherit] object-cover"
            />
            <AvatarFallback className="bg-card text-lg leading-normal text-white md:text-2xl">
              {initials(name)}
            </AvatarFallback>
          </Avatar>
        </div>
        <span
          hidden={!feedback}
          className={cn(
            "connection-feedback max-w-105 text-center text-xs leading-snug font-semibold text-muted-foreground",
            tile.connectionFailed && "text-destructive",
          )}
        >
          {feedbackText}
        </span>
      </div>
      <Button
        asChild
        variant={null}
        className="stream-ended-placeholder absolute inset-0 z-2 grid h-auto place-content-center justify-items-center gap-1.5 rounded-none bg-zinc-950 p-4.5 text-center text-base leading-normal font-normal whitespace-normal text-muted-foreground transition-none hover:bg-zinc-950 focus-visible:ring-0 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary focus-visible:outline-solid"
      >
        <div
          hidden={!streamEnded}
          tabIndex={0}
          role="button"
          onClick={toggleFocus}
          onKeyDown={keyboardToggle}
          aria-label={`${endedTitle}. ${endedNotice}. ${actionLabel}`}
        >
          <EndedIcon className="mb-1 size-7 text-muted-foreground md:size-10" strokeWidth={1.5} />
          <strong className="text-xs font-bold text-foreground sm:text-base">{endedTitle}</strong>
          {/* The grid lays out no space between the lines; their text still needs one. */}{" "}
          <span className="text-xs font-normal text-muted-foreground">{endedNotice}</span>
        </div>
      </Button>
      <Button
        hidden={local || tile.watching}
        type="button"
        className="watch-stream-button absolute top-1/2 left-1/2 z-5 block h-auto min-h-9.5 -translate-x-1/2 -translate-y-1/2 rounded-md border border-white/12 bg-secondary px-3.5 py-0 text-xs font-bold text-foreground shadow-md hover:bg-accent"
        onClick={(event) => {
          event.stopPropagation();
          controller.watchStreamTile(surfaceId);
        }}
      >
        {t(kind === "screen" ? "screen.watch" : "camera.watch")}
      </Button>
      <span
        className={cn(
          "tile-label absolute bottom-2.5 left-2.5 z-5 inline-flex max-w-[calc(100%-var(--spacing)*5)] items-center gap-1.5 rounded-md bg-zinc-900/78 px-2 py-1 text-xs font-semibold backdrop-blur-xs",
          local && "bg-primary",
          focusActive && focused && "hidden",
        )}
      >
        <span className="tile-label-name overflow-hidden text-ellipsis whitespace-nowrap">
          {name}
        </span>
      </span>
    </article>
  );
});

export function StreamGrid() {
  const tiles = useRoomState((s) => s.stage.streamTiles);
  const focusActive = useRoomState((s) => s.stage.streamFocusActive);
  return (
    <div
      id="streamGrid"
      data-count={tiles.length}
      className={cn(
        "stream-grid h-full w-full",
        focusActive ? "focus-mode absolute inset-0 block overflow-hidden" : "hidden",
      )}
    >
      {tiles.map((tile) => (
        <StreamTile key={tile.surfaceId} tile={tile} focusActive={focusActive} />
      ))}
    </div>
  );
}
